import React from 'react';

import cn from 'classnames';
import parse from 'html-react-parser';

import { BaseButton } from 'components/ui/buttons';
import { TemperatureType } from 'types/assessment';

type OptionType = { label: string; value: string };

type QuestionInfoProps = {
  title: string;
  description?: string | null;
  options?: OptionType[];
  selectedOption?: string;
  optional?: boolean;
  changeOption?: (value: string) => void;
  temperature?: { type: TemperatureType; onTypeChange: (temperature: TemperatureType) => void };
};

export const QuestionInfo: React.FC<QuestionInfoProps> = ({
  title,
  description,
  options,
  selectedOption,
  optional,
  changeOption,
  temperature,
}) => {
  return (
    <div className="pb-6">
      <div className="flex flex-row flex-wrap items-center justify-between">
        <h2
          className={cn(
            'text-gray-900 dark:text-white-400 text-lg md:text-[28px] font-semibold leading-[normal]',
            {
              'mb-2 ': description,
            },
            options?.length ? 'max-w-[80%]' : '',
          )}
        >
          {parse(title)}
        </h2>
        {temperature?.onTypeChange && (
          <TemperatureButton value={temperature.type} onChange={temperature.onTypeChange} />
        )}
        {optional && <p className="text-gray-800 text-sm font-medium leading-[130%]">*Optional</p>}
        {options?.length && (
          <div className="flex items-center gap-2 mt-2 md:mt-0 pl-3 ml-auto">
            {options.map((option, idx) => (
              <BaseButton
                key={`${option.value + idx}`}
                scheme="secondary"
                className={`hover:border-sea-serpent/50 ${
                  option.value === selectedOption ? '!bg-mint/60 border-sea-serpent/50' : '!bg-transparent'
                }`}
                onClick={() => changeOption?.(option.value)}
              >
                {option.label}
              </BaseButton>
            ))}
          </div>
        )}
      </div>
      {description && (
        <p className="text-gray-500 dark:text-silver-400 text-sm font-medium leading-[130%]">{parse(description)}</p>
      )}
    </div>
  );
};

type TemperatureButtonProps = {
  value: TemperatureType;
  onChange: (temperature: TemperatureType) => void;
};

const TemperatureButton: React.FC<TemperatureButtonProps> = ({ value, onChange }) => {
  return (
    <div className="ml-auto flex gap-2 pl-3">
      <BaseButton
        scheme={value === TemperatureType.Fahrenheit ? 'active' : 'secondary-light'}
        onClick={() => onChange(TemperatureType.Fahrenheit)}
        className="bg-"
      >
        °F
      </BaseButton>
      <BaseButton
        scheme={value === TemperatureType.Celsius ? 'active' : 'secondary-light'}
        onClick={() => onChange(TemperatureType.Celsius)}
      >
        °С
      </BaseButton>
    </div>
  );
};
